/**
 * Anchored feedback popover (ADR-0074 §3.2).
 *
 * Snapshots route + Atlas-specific context on mount via `useState` initializer
 * so that switching surfaces while the popover is open does NOT change what
 * gets reported (spec §3.2 "Context snapshot at open").
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import {
  readCurrentContext,
  type FeedbackContextEntry,
} from "../lib/feedbackContext.js";
import { captureScreenshot } from "../lib/screenshot.js";
import type {
  FeedbackSeverity,
  FeedbackSubmitBody,
} from "../lib/feedback.js";
import { useToast } from "./Toast.js";

const SEVERITIES: { value: FeedbackSeverity; label: string }[] = [
  { value: "cosmetic", label: "Cosmetic" },
  { value: "minor", label: "Minor" },
  { value: "major", label: "Major" },
  { value: "critical", label: "Critical" },
];

/** Friendly route → label table. Best-effort; falls back to the path itself. */
const ROUTE_LABELS: Array<{ pattern: RegExp; label: string }> = [
  { pattern: /^\/app\/admin\/feedback/, label: "Admin › Feedback" },
  { pattern: /^\/app\/admin\/ai/, label: "Admin › AI" },
  { pattern: /^\/app\/admin\//, label: "Admin" },
  { pattern: /^\/app\/workspace\/pipelines\/[^/]+\/edit/, label: "Pipelines › Editor" },
  { pattern: /^\/app\/workspace\/pipelines/, label: "Pipelines" },
  { pattern: /^\/app\/workspace\/pages/, label: "Pages" },
  { pattern: /^\/app\/workspace\/menus/, label: "Menus" },
  { pattern: /^\/app\/workspace\/pdos/, label: "PDOs" },
  { pattern: /^\/app\/workspace\/schedules/, label: "Schedules" },
  { pattern: /^\/app\/action-items/, label: "Action Items" },
  { pattern: /^\/app\/atlas-query/, label: "Atlas Query" },
  { pattern: /^\/app\/notifications/, label: "Notifications" },
  { pattern: /^\/app\/home/, label: "Home" },
];

function resolveRouteLabel(path: string): string {
  for (const r of ROUTE_LABELS) if (r.pattern.test(path)) return r.label;
  return path;
}

interface ContextSnapshot {
  route_path: string;
  route_label: string;
  active_object: FeedbackContextEntry | null;
  recent_error: { message: string; at: string; kind: "toast" | "api_response" | "console_error" } | null;
  browser: {
    user_agent: string;
    viewport_width: number;
    viewport_height: number;
    timezone: string;
    locale: string;
  };
}

function captureContextSnapshot(): ContextSnapshot {
  const path = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
  return {
    route_path: path,
    route_label: resolveRouteLabel(path),
    active_object: readCurrentContext(),
    recent_error: null, // toast/error capture wiring is a future extension; flagged in spec §10
    browser: {
      user_agent: navigator.userAgent,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      locale: navigator.language,
    },
  };
}

function activeObjectChip(entry: FeedbackContextEntry): string {
  switch (entry.kind) {
    case "pipeline": return `Pipeline: ${entry.displayName ?? "(unnamed)"}`;
    case "node": return `Node: ${entry.displayName ?? "(unnamed)"}`;
    case "run": return `Run started ${entry.displayName ?? "recently"}`;
    case "action_item": return `Action Item: ${entry.displayName ?? "(untitled)"}`;
    case "query": return `Query: ${entry.displayName ?? "(unnamed)"}`;
  }
}

export function FeedbackPopover(props: {
  anchor: HTMLElement | null;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Snapshot context on mount; subsequent reads of the registry don't affect submission.
  const [snapshot] = useState<ContextSnapshot>(() => captureContextSnapshot());

  const [severity, setSeverity] = useState<FeedbackSeverity>("minor");
  const [message, setMessage] = useState("");
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flipUp, setFlipUp] = useState(false);

  const toast = useToast();

  useEffect(() => { textareaRef.current?.focus(); }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    const anchorRect = props.anchor?.getBoundingClientRect();
    if (!el || !anchorRect) return;
    const popoverHeight = el.offsetHeight;
    const spaceBelow = window.innerHeight - anchorRect.bottom - 8;
    const spaceAbove = anchorRect.top - 8;
    setFlipUp(popoverHeight > spaceBelow && spaceAbove > spaceBelow);
  }, [props.anchor]);

  // Outside click + Escape close.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (ref.current?.contains(target)) return;
      if (props.anchor?.contains(target)) return;
      props.onClose();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") props.onClose();
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [props]);

  function severityKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const i = SEVERITIES.findIndex((s) => s.value === severity);
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = SEVERITIES[(i + 1) % SEVERITIES.length];
      setSeverity(next.value);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = SEVERITIES[(i - 1 + SEVERITIES.length) % SEVERITIES.length];
      setSeverity(next.value);
    }
  }

  async function submit() {
    const trimmed = message.trim();
    if (trimmed.length === 0 || busy) return;
    setBusy(true);
    setError(null);

    let screenshot: { mime_type: string; base64_bytes: string } | undefined;
    let screenshotCaptureFailed = false;
    if (includeScreenshot) {
      // Hide the popover so it isn't part of the screenshot. Keep it hidden
      // through the rest of the submit flow (success → unmount; failure →
      // restored in the catch below) so the user sees ONE transition, not
      // hide → show → hide.
      if (ref.current) ref.current.style.visibility = "hidden";
      // Wait one paint so the browser has actually applied visibility:hidden
      // before html2canvas snapshots the DOM.
      await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
      const result = await captureScreenshot();
      if (result) {
        screenshot = { mime_type: result.mime_type, base64_bytes: result.base64_bytes };
      } else {
        screenshotCaptureFailed = true;
      }
    }

    const body: FeedbackSubmitBody = {
      severity,
      message: trimmed,
      ...(screenshot ? { screenshot } : {}),
      context: {
        route_path: snapshot.route_path,
        route_label: snapshot.route_label,
        ...(snapshot.active_object
          ? {
              active_object: {
                kind: snapshot.active_object.kind,
                id: snapshot.active_object.id,
                display_name: snapshot.active_object.displayName,
              },
            }
          : {}),
        ...(snapshot.recent_error ? { last_error: snapshot.recent_error } : {}),
        browser: snapshot.browser,
        build: { hub_sha: null, console_sha: null },
        ...(screenshotCaptureFailed ? { screenshot_capture_failed: true } : {}),
      },
    };

    try {
      await apiFetch("/api/tenant/feedback", { method: "POST", body });
      toast.push({ kind: "success", message: "Thanks — feedback sent." });
      props.onClose();
    } catch (e) {
      // Restore visibility so the user can read the inline error and retry.
      if (ref.current) ref.current.style.visibility = "";
      if (e instanceof ApiRequestError && e.code === "rate_limited") {
        setError("Too many submissions just now — please try again in a few minutes.");
      } else {
        setError("Couldn't send. Please try again.");
      }
      setBusy(false);
    }
  }

  // Position relative to anchor.
  const rect = props.anchor?.getBoundingClientRect();
  const width = 420;
  const popoverHeight = ref.current?.offsetHeight ?? 0;
  const style: React.CSSProperties = rect
    ? {
        position: "fixed",
        top: flipUp ? Math.max(8, rect.top - popoverHeight - 4) : rect.bottom + 4,
        left: Math.max(8, Math.min(window.innerWidth - width - 8, rect.right - width)),
        width,
        maxHeight: "80vh",
        overflowY: "auto",
        zIndex: 50,
        background: "var(--surface, #fff)",
        border: "1px solid var(--border, #ddd)",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        padding: 16,
      }
    : { display: "none" };

  const trimmed = message.trim();
  const sendDisabled = busy || trimmed.length === 0;

  return (
    <div ref={ref} role="dialog" aria-label="Send feedback" style={style}>
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Send feedback</div>

      <div
        role="radiogroup"
        aria-label="Severity"
        onKeyDown={severityKeyDown}
        style={{ display: "flex", gap: 6, marginBottom: 12 }}
      >
        {SEVERITIES.map((s) => (
          <button
            key={s.value}
            type="button"
            role="radio"
            aria-checked={severity === s.value}
            tabIndex={severity === s.value ? 0 : -1}
            className={severity === s.value ? "btn btnPrimary" : "btn"}
            onClick={() => setSeverity(s.value)}
            style={{ flex: 1 }}
            disabled={busy}
          >
            {s.label}
          </button>
        ))}
      </div>

      <textarea
        ref={textareaRef}
        className="input"
        rows={4}
        placeholder="What's going on?"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        disabled={busy}
        style={{ width: "100%", minHeight: 96, maxHeight: 240, marginBottom: 12, resize: "vertical" }}
        aria-label="Feedback message"
      />

      <label className="field" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={includeScreenshot}
          onChange={(e) => setIncludeScreenshot(e.target.checked)}
          disabled={busy}
        />
        <span>Include a screenshot of this page</span>
      </label>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        <span className="pill">{snapshot.route_label}</span>
        {snapshot.active_object ? (
          <span className="pill" title={snapshot.active_object.id}>{activeObjectChip(snapshot.active_object)}</span>
        ) : null}
        {snapshot.recent_error ? (
          <span className="pill pillWarn">Recent error: {snapshot.recent_error.message.slice(0, 60)}</span>
        ) : null}
      </div>

      {error ? <div className="help" style={{ color: "var(--danger-text)", marginBottom: 8 }}>{error}</div> : null}

      <div className="actions" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="btn" onClick={props.onClose} disabled={busy}>Cancel</button>
        <button
          type="button"
          className="btn btnPrimary"
          onClick={() => void submit()}
          disabled={sendDisabled}
        >
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}
